import { useEffect, useState } from "react";
import { X, Trash2, Globe, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FORMATER, SESSION_STATUS } from "./eventosUtils";
import { ReadinessBadge, ReadinessListe } from "./Readiness";

const Seksjon = ({ label, children }) => (
  <div className="mt-6">
    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400 mb-2">{label}</p>
    {children}
  </div>
);

const Felt = ({ label, verdi, onLagre, testid, textarea, type = "text" }) => {
  const [v, setV] = useState(verdi ?? "");
  useEffect(() => { setV(verdi ?? ""); }, [verdi]);
  const Tag = textarea ? "textarea" : "input";
  return (
    <label className="adm-label">
      {label}
      <Tag
        type={type}
        value={v}
        rows={textarea ? 3 : undefined}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => v !== (verdi ?? "") && onLagre(v)}
        className="adm-input"
        data-testid={testid}
      />
    </label>
  );
};

export default function SessionDrawer({ sess, spaces, speakers, members, aktor, onPatch, onDelete, onLukk }) {
  const [saker, setSaker] = useState(null);
  const [nySak, setNySak] = useState("");

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onLukk(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onLukk]);

  useEffect(() => {
    let aktiv = true;
    api.get("/admin/tasks").then(({ data }) => {
      if (!aktiv) return;
      setSaker((data.tasks || []).filter((t) => (t.linkedEntities || []).some((le) => le.type === "session" && le.id === sess.id)));
    }).catch(() => setSaker([]));
    return () => { aktiv = false; };
  }, [sess.id]);

  const lagNySak = async () => {
    const t = nySak.trim();
    if (!t) return;
    try {
      const { data } = await api.post("/admin/tasks", {
        title: t,
        actor: aktor,
        linkedEntities: [{ type: "session", id: sess.id, label: sess.internalTitle }],
      });
      setSaker((prev) => [data.task, ...(prev || [])]);
      setNySak("");
      toast.success("Sak opprettet og koblet");
    } catch (e) {
      toast.error("Kunne ikke opprette sak");
    }
  };

  const toggleSpeaker = (pid) => {
    const ids = sess.speakerParticipationIds || [];
    onPatch({ speakerParticipationIds: ids.includes(pid) ? ids.filter((x) => x !== pid) : [...ids, pid] });
  };

  const aktivitet = [...(sess.activity || [])].reverse();

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="session-drawer">
      <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={onLukk} />
      <aside className="sak-drawer relative w-full max-w-[600px] h-full bg-white shadow-2xl border-l border-[#E7E4DB] flex flex-col">
        <div className="px-6 pt-5 pb-4 border-b border-neutral-100 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <Felt label="" verdi={sess.internalTitle} onLagre={(v) => v.trim() && onPatch({ internalTitle: v })} testid="session-tittel-input" />
            <div className="flex items-center gap-1 shrink-0 mt-1">
              <button
                type="button"
                onClick={() => onPatch({ published: !sess.published })}
                className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium rounded-lg px-2.5 py-1.5 border transition-colors ${
                  sess.published ? "bg-neutral-900 text-white border-neutral-900" : "text-neutral-500 border-[#DED9CD] hover:text-neutral-800"
                }`}
                data-testid="session-publiser-btn"
                title="Styrer om sessionen vises i offentlig program"
              >
                <Globe size={12} /> {sess.published ? "Publisert" : "Utkast"}
              </button>
              <button type="button" onClick={onDelete} className="p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" data-testid="session-slett-btn">
                <Trash2 size={15} />
              </button>
              <button type="button" onClick={onLukk} className="p-1.5 text-neutral-400 hover:text-neutral-800 hover:bg-neutral-100 rounded-md transition-colors" data-testid="session-drawer-lukk">
                <X size={17} />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-2">
            <div className="flex bg-neutral-100 rounded-lg p-0.5">
              {SESSION_STATUS.map((s) => (
                <button
                  key={s.k}
                  type="button"
                  onClick={() => sess.status !== s.k && onPatch({ status: s.k })}
                  className={`flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-md transition-all ${
                    sess.status === s.k ? "bg-white shadow-sm text-neutral-900" : "text-neutral-500 hover:text-neutral-800"
                  }`}
                  data-testid={`session-status-${s.k}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
                  {s.label}
                </button>
              ))}
            </div>
            <ReadinessBadge computed={sess.computed} size="md" />
          </div>
          {(sess.conflicts || []).length > 0 && (
            <p className="mt-2 text-[11.5px] font-medium text-red-600 bg-red-50 rounded-lg px-2.5 py-1.5" data-testid="session-konflikt">
              ⚠ Romkonflikt: overlapper med {sess.conflicts.length} annen/andre session(er) i samme rom
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <Seksjon label="Planlegging">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <label className="adm-label">
                Start
                <input type="time" defaultValue={sess.start || ""} onBlur={(e) => e.target.value !== sess.start && onPatch({ start: e.target.value })} className="adm-input" data-testid="session-start-input" />
              </label>
              <label className="adm-label">
                Slutt
                <input type="time" defaultValue={sess.end || ""} onBlur={(e) => e.target.value !== sess.end && onPatch({ end: e.target.value })} className="adm-input" data-testid="session-slutt-input" />
              </label>
              <label className="adm-label">
                Rom
                <select value={sess.spaceId || ""} onChange={(e) => onPatch({ spaceId: e.target.value || null })} className="adm-input" data-testid="session-space-select">
                  <option value="">Ikke satt</option>
                  {(spaces || []).map((sp) => (
                    <option key={sp.id} value={sp.id}>{sp.name}</option>
                  ))}
                </select>
              </label>
              <label className="adm-label">
                Format
                <select value={sess.format || "talk"} onChange={(e) => onPatch({ format: e.target.value })} className="adm-input" data-testid="session-format-select">
                  {FORMATER.map((f) => (
                    <option key={f.k} value={f.k}>{f.label}</option>
                  ))}
                </select>
              </label>
            </div>
          </Seksjon>

          <Seksjon label="Readiness">
            <ReadinessListe computed={sess.computed} onSett={(r) => onPatch({ readiness: r })} testidPrefix="session" />
          </Seksjon>

          <Seksjon label="Offentlig innhold (projiseres til nettsiden ved publisering)">
            <div className="grid grid-cols-1 gap-3">
              <Felt label="Offentlig tittel" verdi={sess.publicTitle} onLagre={(v) => onPatch({ publicTitle: v })} testid="session-public-tittel" />
              <Felt label="Beskrivelse" verdi={sess.description} onLagre={(v) => onPatch({ description: v })} testid="session-beskrivelse" textarea />
              <div className="grid grid-cols-2 gap-3">
                <Felt label="Track" verdi={sess.track} onLagre={(v) => onPatch({ track: v })} testid="session-track" />
                <label className="flex items-center gap-2 mt-6 text-[12.5px] text-neutral-700">
                  <input
                    type="checkbox"
                    checked={!!sess.preliminary}
                    onChange={(e) => onPatch({ preliminary: e.target.checked })}
                    className="w-4 h-4 accent-[#0f1115]"
                    data-testid="session-preliminary"
                  />
                  Merk som foreløpig
                </label>
              </div>
            </div>
          </Seksjon>

          <Seksjon label="Personer">
            <p className="text-[11px] text-neutral-400 mb-2">Speakers på sessionen (klikk for å koble av/på):</p>
            <div className="flex flex-wrap gap-1.5">
              {(speakers || []).map((sp) => {
                const valgt = (sess.speakerParticipationIds || []).includes(sp.id);
                return (
                  <button
                    key={sp.id}
                    type="button"
                    onClick={() => toggleSpeaker(sp.id)}
                    className={`text-[11.5px] font-medium rounded-full px-2.5 py-1 border transition-colors ${
                      valgt ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-500 border-[#DED9CD] hover:border-neutral-400"
                    }`}
                    data-testid={`session-speaker-${sp.id}`}
                  >
                    {sp.name}
                    {sp.stage !== "confirmed" && <span className={valgt ? " text-amber-300" : " text-amber-500"}> ·ubekreftet</span>}
                  </button>
                );
              })}
            </div>
            <label className="adm-label mt-3 w-48">
              Intern ansvarlig
              <select value={sess.ownerId || ""} onChange={(e) => onPatch({ ownerId: e.target.value || null })} className="adm-input" data-testid="session-owner-select">
                <option value="">Ingen</option>
                {(members || []).map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </label>
          </Seksjon>

          <Seksjon label="Produksjon (internt)">
            <div className="grid grid-cols-2 gap-3">
              <Felt label="Sceneoppsett" verdi={sess.production?.stageSetup} onLagre={(v) => onPatch({ production: { stageSetup: v } })} testid="session-prod-scene" />
              <Felt label="AV-behov" verdi={sess.production?.avRequirements} onLagre={(v) => onPatch({ production: { avRequirements: v } })} testid="session-prod-av" />
            </div>
            <div className="mt-3">
              <Felt label="Produksjonsnotater" verdi={sess.production?.notes} onLagre={(v) => onPatch({ production: { notes: v } })} testid="session-prod-notater" textarea />
            </div>
          </Seksjon>

          <Seksjon label={`Saker (${saker == null ? "…" : saker.length})`}>
            {(saker || []).map((t) => (
              <p key={t.id} className="flex items-center gap-2 text-[12.5px] py-1">
                <span className={`w-1.5 h-1.5 rounded-full ${t.status === "done" ? "bg-emerald-500" : "bg-amber-500"}`} />
                <span className={t.status === "done" ? "text-neutral-400 line-through" : "text-neutral-700"}>{t.title}</span>
                {t.dueDate && <span className="text-[10.5px] text-neutral-400 ml-auto">{t.dueDate}</span>}
              </p>
            ))}
            <div className="flex items-center gap-2 mt-1.5">
              <Plus size={13} className="text-neutral-300 shrink-0" />
              <input
                value={nySak}
                onChange={(e) => setNySak(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lagNySak(); } }}
                placeholder="Ny sak koblet til denne sessionen — Enter"
                className="flex-1 text-[12.5px] py-1 bg-transparent text-neutral-900 focus:outline-none placeholder:text-neutral-300"
                data-testid="session-ny-sak-input"
              />
            </div>
          </Seksjon>

          {aktivitet.length > 0 && (
            <Seksjon label="Aktivitet">
              <div className="space-y-1">
                {aktivitet.slice(0, 8).map((a, i) => (
                  <p key={i} className="text-[11px] text-neutral-400 leading-relaxed">
                    <span className="text-neutral-600 font-medium">{a.actor}</span> · {a.text}
                    <span className="ml-1.5 text-neutral-300">{new Date(a.at).toLocaleDateString("nb-NO", { day: "numeric", month: "short" })}</span>
                  </p>
                ))}
              </div>
            </Seksjon>
          )}
        </div>
      </aside>
    </div>
  );
}
